/**
 * Sub-components for ArticleDocumentEditor render sections.
 *
 * Extracted to keep the main editor under the 300-line limit.
 */

import React, { useState } from 'react';
import { Field, PencilIcon, SectionHeading, StringListEditor, TextArea, TextInput, TrashIcon } from './ArticleDocumentEditorFields';
import type { ArticleDocument, ArticleSection, FaqItem, InternalLinkCandidate, ReferenceSource } from './types';

// ---------------------------------------------------------------------------
// Content Research Section
// ---------------------------------------------------------------------------

interface ContentResearchProps {
  sectionId: string;
  contentResearch: ArticleDocument['content_research'];
  updateLink: (index: number, field: keyof InternalLinkCandidate, value: unknown) => void;
  addLink: () => void;
  removeLink: (index: number) => void;
  updateRef: (index: number, field: keyof ReferenceSource, value: string) => void;
  addRef: () => void;
  removeRef: (index: number) => void;
}

export function ContentResearchEditor({
  sectionId,
  contentResearch,
  updateLink,
  addLink,
  removeLink,
  updateRef,
  addRef,
  removeRef,
}: ContentResearchProps) {
  const [expandedLinks, setExpandedLinks] = useState<Set<number>>(new Set());
  const [expandedRefs, setExpandedRefs] = useState<Set<number>>(new Set());

  return (
    <SectionHeading id={sectionId} title="Content Research">
      {/* Internal Link Candidates */}
      <div>
        <span className="text-xs text-gray-500 font-medium">
          Internal link candidates ({(contentResearch?.internal_link_candidates || []).length})
        </span>
        <div className="space-y-1 mt-1.5">
          {(contentResearch?.internal_link_candidates || []).map((link, i) => {
            const isExpanded = expandedLinks.has(i);
            return (
              <div key={i} className="border border-gray-200 rounded px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-800 truncate">{link.target_page || 'Untitled'}</div>
                    <div className="text-xs text-gray-400 truncate">{link.url}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      className={`p-1 rounded hover:bg-gray-100 ${isExpanded ? 'text-blue-600' : 'text-gray-400'}`}
                      onClick={() => setExpandedLinks((prev) => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i); else next.add(i);
                        return next;
                      })}
                    >
                      <PencilIcon />
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      onClick={() => removeLink(i)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    <Field label="Page title">
                      <TextInput value={link.target_page} onChange={(v) => updateLink(i, 'target_page', v)} placeholder="Page title" />
                    </Field>
                    <Field label="URL">
                      <TextInput value={link.url} onChange={(v) => updateLink(i, 'url', v)} placeholder="https://..." />
                    </Field>
                    <Field label="Suggested anchor text">
                      <TextInput value={link.suggested_anchor || ''} onChange={(v) => updateLink(i, 'suggested_anchor', v)} placeholder="Anchor text" />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={addLink}>
            + Add link
          </button>
        </div>
      </div>

      {/* Reference Sources */}
      <div className="mt-3">
        <span className="text-xs text-gray-500 font-medium">
          Reference sources ({(contentResearch?.reference_sources || []).length})
        </span>
        <div className="space-y-1 mt-1.5">
          {(contentResearch?.reference_sources || []).map((ref, i) => {
            const isExpanded = expandedRefs.has(i);
            return (
              <div key={i} className="border border-gray-200 rounded px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-800 truncate">{ref.source || 'Untitled source'}</div>
                    <div className="text-xs text-gray-400 truncate">{ref.url || ref.use}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      className={`p-1 rounded hover:bg-gray-100 ${isExpanded ? 'text-blue-600' : 'text-gray-400'}`}
                      onClick={() => setExpandedRefs((prev) => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i); else next.add(i);
                        return next;
                      })}
                    >
                      <PencilIcon />
                    </button>
                    <button
                      type="button"
                      className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      onClick={() => removeRef(i)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    <Field label="Source name">
                      <TextInput value={ref.source} onChange={(v) => updateRef(i, 'source', v)} placeholder="Source name" />
                    </Field>
                    <Field label="URL">
                      <TextInput value={ref.url} onChange={(v) => updateRef(i, 'url', v)} placeholder="https://..." />
                    </Field>
                    <Field label="What to cite / how to use">
                      <TextArea value={ref.use} onChange={(v) => updateRef(i, 'use', v)} rows={2} placeholder="How this source should be used in the article" />
                    </Field>
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" className="text-xs text-blue-600 hover:underline" onClick={addRef}>
            + Add reference
          </button>
        </div>
      </div>
    </SectionHeading>
  );
}

// ---------------------------------------------------------------------------
// Article Sections Editor
// ---------------------------------------------------------------------------

interface ArticleSectionsProps {
  sectionId: string;
  sections: ArticleSection[];
  updateSection: (index: number, field: keyof ArticleSection, value: unknown) => void;
  addSection: () => void;
  removeSection: (index: number) => void;
}

export function ArticleSectionsEditor({ sectionId, sections, updateSection, addSection, removeSection }: ArticleSectionsProps) {
  return (
    <SectionHeading id={sectionId} title={`Sections (${sections.length})`}>
      {sections.map((section, i) => (
        <div key={section.id} className="border border-gray-200 rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">{section.heading_level}</span>
              {section.heading_level === 'intro' ? (
                <span className="text-sm text-gray-600 italic">Introduction</span>
              ) : (
                <input
                  type="text"
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-medium"
                  value={section.heading || ''}
                  onChange={(e) => updateSection(i, 'heading', e.target.value)}
                  placeholder="Section heading"
                />
              )}
            </div>
            <button
              type="button"
              className="text-xs text-red-500 hover:text-red-700"
              onClick={() => removeSection(i)}
            >
              Remove
            </button>
          </div>
          {section.purpose != null && (
            <Field label="Purpose">
              <TextArea value={section.purpose || ''} onChange={(v) => updateSection(i, 'purpose', v)} rows={2} />
            </Field>
          )}
          {section.key_points != null && (
            <Field label="Key points">
              <StringListEditor
                items={section.key_points || []}
                onChange={(v) => updateSection(i, 'key_points', v)}
                placeholder="Key point"
              />
            </Field>
          )}
          {section.body != null && (
            <Field label="Body (markdown)">
              <TextArea
                value={section.body || ''}
                onChange={(v) => updateSection(i, 'body', v)}
                rows={8}
                placeholder="Section content..."
              />
            </Field>
          )}
          {(section.internal_links || []).length > 0 && (
            <div>
              <span className="text-xs text-gray-400">Internal links ({section.internal_links.length})</span>
              <div className="text-xs text-gray-500 mt-0.5">
                {section.internal_links.map((l, j) => (
                  <div key={j}>{l.target_page} — {l.anchor_context}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={addSection}
      >
        + Add section
      </button>
    </SectionHeading>
  );
}

// ---------------------------------------------------------------------------
// FAQ Editor
// ---------------------------------------------------------------------------

interface FaqEditorProps {
  sectionId: string;
  faq: FaqItem[];
  updateFaq: (index: number, field: keyof FaqItem, value: string) => void;
  addFaq: () => void;
  removeFaq: (index: number) => void;
}

export function FaqEditor({ sectionId, faq, updateFaq, addFaq, removeFaq }: FaqEditorProps) {
  return (
    <SectionHeading id={sectionId} title={`FAQ (${faq.length})`}>
      {faq.map((item, i) => (
        <div key={i} className="border border-gray-200 rounded p-2 space-y-1">
          <div className="flex items-center justify-between">
            <Field label="Question">
              <TextInput value={item.question} onChange={(v) => updateFaq(i, 'question', v)} />
            </Field>
            <button
              type="button"
              className="text-xs text-red-500 hover:text-red-700 ml-2"
              onClick={() => removeFaq(i)}
            >
              Remove
            </button>
          </div>
          <Field label="Answer">
            <TextArea value={item.answer || ''} onChange={(v) => updateFaq(i, 'answer', v)} rows={2} />
          </Field>
        </div>
      ))}
      <button
        type="button"
        className="text-xs text-blue-600 hover:underline"
        onClick={addFaq}
      >
        + Add FAQ
      </button>
    </SectionHeading>
  );
}
